import { useEffect, useState } from 'react';
import { MdCheckCircle } from 'react-icons/md';
import { getPricingPlans, createSubscription, verifySubscription, fetchProfile } from 'services/apiService';
import { loadRazorpay } from 'services/razorpayService';
import { toast } from 'react-toastify';

const PricingCard = () => {
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [interval, setInterval] = useState<string>('monthly');
  const [userProfile, setUserProfile] = useState<any>(null);
  const [subscribingPlan, setSubscribingPlan] = useState<string | false>(false);

  useEffect(() => {
    fetchProfile()
      .then(setUserProfile)
      .catch(() => {});
  }, []);

  useEffect(() => {
    getPricingPlans()
      .then(data => { setPlans(data.all_pricing); setLoading(false); })
      .catch(err => { setError(err); setLoading(false); });
  }, []);

  const activePlanName = userProfile?.organization?.subscription_plan?.name;

  const handleSubscription = async (plan: any) => {
    setSubscribingPlan(plan.razorpay_plan_id);
    try {
      const subscriptionData = await createSubscription(plan.razorpay_plan_id);
      const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;

      loadRazorpay(() => {
        const options = {
          key: razorpayKey,
          subscription_id: subscriptionData.subscription_id,
          name: 'ScaleLens Analytics Platform',
          description: `Activate the ${plan.name} Plan`,
          handler: async (response: any) => {
            try {
              await verifySubscription(
                response.razorpay_subscription_id,
                response.razorpay_payment_id,
                response.razorpay_signature
              );
              toast.success('Subscription successfully activated.');
              window.location.href = '/dashboard/profile';
            } catch {
              toast.error('Payment verification failed. Please contact support.');
            }
          },
          prefill: {
            name: userProfile?.full_name || '',
            email: userProfile?.email || '',
            contact: userProfile?.contact_number || '',
          },
          theme: { color: '#0F766E' },
        };
        const razorpayInstance = new (window as any).Razorpay(options);
        razorpayInstance.open();
      });
    } catch {
      toast.error('Subscription creation failed. Please try again.');
    } finally {
      setSubscribingPlan(false);
    }
  };

  if (loading) return <p className="text-[#6B7280] py-8 text-center">Loading plans…</p>;
  if (error)   return <p className="text-red-500 py-8 text-center">Error fetching pricing plans.</p>;

  const filteredPlans = plans.filter(plan => plan.interval === interval);
  const monthlyPlan = plans.find((p: any) => p.interval === 'monthly');
  const yearlySavings = (plan: any): number | null => {
    if (plan.interval !== 'yearly' || !monthlyPlan) return null;
    const savings = Math.round(parseFloat(monthlyPlan.amount_in_usd) * 12 - parseFloat(plan.amount_in_usd));
    return savings > 0 ? savings : null;
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#1F2937] mb-1">Choose Your Plan</h2>
        <p className="text-[#6B7280] text-sm">Select the plan that works best for your team.</p>
      </div>

      {/* Toggle */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex bg-[#F1F5F9] rounded-lg p-1 gap-1">
          <button
            onClick={() => setInterval('monthly')}
            className={`px-6 py-2 rounded-md text-sm font-semibold transition-all duration-150 ${
              interval === 'monthly'
                ? 'bg-white text-brand-500 shadow-sm'
                : 'text-[#6B7280] hover:text-[#1F2937]'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setInterval('yearly')}
            className={`px-6 py-2 rounded-md text-sm font-semibold transition-all duration-150 ${
              interval === 'yearly'
                ? 'bg-white text-brand-500 shadow-sm'
                : 'text-[#6B7280] hover:text-[#1F2937]'
            }`}
          >
            Yearly
          </button>
        </div>
      </div>

      {/* Plan cards */}
      {filteredPlans.length === 0 ? (
        <p className="text-center text-[#6B7280]">No {interval} plans available.</p>
      ) : (
        <div className={`grid gap-6 ${
          filteredPlans.length === 1 ? 'grid-cols-1 max-w-sm mx-auto' :
          filteredPlans.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
          'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        }`}>
          {filteredPlans.map((plan, index) => {
            const isCurrentPlan = activePlanName === plan.name;
            return (
            <div
              key={index}
              className={`bg-white border rounded-xl p-6 flex flex-col shadow-sm transition-shadow ${
                isCurrentPlan
                  ? 'border-brand-500 ring-2 ring-brand-500/20 hover:shadow-md'
                  : 'border-[#E4E7EB] hover:shadow-md'
              }`}
            >
              {/* Plan name & tagline */}
              <div className="mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-bold text-[#1F2937]">{plan.name}</h3>
                  {isCurrentPlan && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand-500/10 px-2.5 py-0.5 text-xs font-semibold text-brand-600">
                      <MdCheckCircle className="h-3.5 w-3.5" /> Current Plan
                    </span>
                  )}
                </div>
                {plan.tag_line_for_plan && (
                  <p className="text-sm text-[#6B7280] mt-1">{plan.tag_line_for_plan}</p>
                )}
              </div>

              {/* Price */}
              <div className="mb-5">
                <span className="text-4xl font-bold text-[#1F2937]">
                  ${plan.amount_in_usd}
                </span>
                <span className="text-sm text-[#6B7280] ml-1">/ {plan.interval}</span>
                {yearlySavings(plan) !== null && (
                  <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                    🎉 Save ${yearlySavings(plan)} vs monthly billing
                  </p>
                )}
              </div>

              {/* Features */}
              {plan.descriptions && plan.descriptions.length > 0 && (
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.descriptions.map((desc: string, i: number) => (
                    <li key={i} className="flex items-start gap-2">
                      <MdCheckCircle className="text-brand-500 mt-0.5 shrink-0" />
                      <span className="text-sm text-[#6B7280]">{desc}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* CTA */}
              {isCurrentPlan ? (
                <div className="mt-auto flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-brand-600">
                  <MdCheckCircle className="h-4 w-4 text-brand-500" />
                  You are subscribed to this plan
                </div>
              ) : (
                <button
                  onClick={() => handleSubscription(plan)}
                  disabled={subscribingPlan === plan.razorpay_plan_id}
                  className="mt-auto w-full rounded-lg bg-brand-500 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 transition-colors disabled:opacity-60 flex items-center justify-center"
                >
                  {subscribingPlan === plan.razorpay_plan_id ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  ) : 'Subscribe Now'}
                </button>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PricingCard;
